import React, {useEffect, useReducer, useState} from "react";

import {Button, Flashbar, Grid, Header, Popover, SpaceBetween, Tabs} from "@amzn/awsui-components-react";
import {OBLIGATION_STATUS} from "../common/constants";
import ObligationDetails from "../common/ObligationDetails";
import ExceptionSummary from "../common/ExceptionSummary";
import ExpandableSection from "@amzn/awsui-components-react/polaris/expandable-section";
import DSTSummary from "./DSTSummary";
import EntityBreakdown from "./EntityBreakdown";
import ViewAttachments from "../common/attachment/ViewAttachments";
import {getAcknowledgeData, getUserAlias, isObligationUnderReview} from "../../utils/utils";
import DSTChecklists from "./DSTChecklists";
import {ADMINISTRATOR_ROLE, APPROVER_ROLE, REVIEWER_ROLE} from "../../../../common/constants/constants";
import DSTInformation from "./DSTInformation";
import {checkUserPermissions, modifyWorkflowStatus} from "../redux/exceptionAPIs";
import DSTAdjustmentAudit from "./DSTAdjustmentAudit";
import {RejectReturnModal} from "../common/RejectReturnModal";
import {dstReducer, dstReducerInit, INITIAL_STATE} from "./DSTReducer";


function DSTException(props) {

    const {
        itemData,
        updateWorkflowStatus,
        updateWorkflowStatusDispatcher,
    } = props;

    const [dstStateMap, dstDispatcher] = useReducer(dstReducer,
        INITIAL_STATE, dstReducerInit);

    const [activeTabId, setActiveTabId] = useState('first');
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
    const [lastUpdatedOn, setLastUpdatedOn] = useState('-');
    const [entityFilter, setEntityFilter] = useState();
    const onIngestionClickHandler = () => {
        setActiveTabId("tenth");
    };

    const ENTITY_BREAKDOWN_TAB_ID = 'second'
    const stepsInitialState = getAcknowledgeData(itemData?.acknowledgementMap);
    const [stepsState, setStepsState] = useState(stepsInitialState);
    const [obligationStatus, setObligationStatus] = useState(itemData?.status);
    const isUnderReview = isObligationUnderReview(obligationStatus);
    const [userRole, setUserRole] = useState(REVIEWER_ROLE);
    const [approveButtonDisabled, setApproveButtonDisabled] = useState(false);

    useEffect(() => {
        checkUserPermissions(setUserRole);
    }, []);


    // Send for approval
    const isSendForApproveButtonDisabled = () => {
        return !(
            isUnderReview
            && stepsState.checklistStep.completed
        );
    };

    const generateUpdatingStatusRequest = (statusValue) => {
        return {
            parentWorkflowId: itemData?.parentWorkflowId,
            status: statusValue,
            acknowledgementStructureMap: {},
        }
    }

    const sendForApproveButtonClickHandler = () => {
        const onSuccess = async () => {
            setApproveButtonDisabled(true);
            setObligationStatus(OBLIGATION_STATUS.UnderApproval)
        };
        modifyWorkflowStatus(updateWorkflowStatusDispatcher,
            generateUpdatingStatusRequest(OBLIGATION_STATUS.UnderApproval),
            onSuccess);
    };

    // Approve return
    const approveButtonClickHandler = (event) => {
        event.stopPropagation();
        const onSuccess = async () => {
            setObligationStatus(OBLIGATION_STATUS.UnderFiling)
        };
        modifyWorkflowStatus(updateWorkflowStatusDispatcher,
            generateUpdatingStatusRequest(OBLIGATION_STATUS.UnderFiling),
            onSuccess);
    };

    const isApproveButtonDisable = () => {
        return !(
            obligationStatus === OBLIGATION_STATUS.UnderApproval
            && checklistState.approver.option1 && checklistState.approver.option2
            && checklistState.approver.option3 && checklistState.approver.option4
        );
    };

    // Reject Return
    const [rejectConfirmModal, setRejectConfirmModal] = useState(false);

    const rejectButtonClickHandler = (event) => {
        event.stopPropagation();
        setRejectConfirmModal(true);
    };

    const rejectConfirmHandler = (rejectReason) => {
        const onSuccess = async () => {
            setObligationStatus(OBLIGATION_STATUS.UnderReview)
        };
        modifyWorkflowStatus(updateWorkflowStatusDispatcher,
            generateUpdatingStatusRequest(OBLIGATION_STATUS.UnderReview),
            onSuccess);
    };

    const [checklistState, setChecklistState] = useState({
        preparer: {
            option1: stepsState.checklistStep.completed,
            option2: stepsState.checklistStep.completed,
            option3: stepsState.checklistStep.completed,
            option4: stepsState.checklistStep.completed,
            option5: stepsState.checklistStep.completed,
            option6: stepsState.checklistStep.completed,
        },
        approver: {
            option1: obligationStatus === OBLIGATION_STATUS.UnderAdjustment || OBLIGATION_STATUS.UnderFiling || obligationStatus === OBLIGATION_STATUS.Filed,
            option2: obligationStatus === OBLIGATION_STATUS.UnderAdjustment || OBLIGATION_STATUS.UnderFiling || obligationStatus === OBLIGATION_STATUS.Filed,
            option3: obligationStatus === OBLIGATION_STATUS.UnderAdjustment || OBLIGATION_STATUS.UnderFiling || obligationStatus === OBLIGATION_STATUS.Filed,
            option4: obligationStatus === OBLIGATION_STATUS.UnderAdjustment || OBLIGATION_STATUS.UnderFiling || obligationStatus === OBLIGATION_STATUS.Filed,
        }
    });

    const setStepStateHandler = async (step) => {
        const userAlias = await getUserAlias();
        setStepsState({
            ...stepsState,
            [step]: {completed: true, user: userAlias, updatedOn: new Date().toLocaleDateString()},
        });
    };

    const exceptionSummaryTableProp = [
        {
            'step': 'Checklists',
            'status': stepsState.checklistStep.completed ? 'Completed' : 'Pending',
            'person': stepsState.checklistStep.user,
            'date': stepsState.checklistStep.updatedOn,
        },
    ];

    const reviewerButtons = () => {
        return (
            <SpaceBetween direction="horizontal" size="xs">
                <Button disabled={isSendForApproveButtonDisabled() || approveButtonDisabled}
                        onClick={sendForApproveButtonClickHandler}>Send for approval</Button>
                {(isSendForApproveButtonDisabled() || approveButtonDisabled) &&
                    <Popover
                        position="top"
                        size="small"
                        triggerType="custom"
                        content={
                            <div>
                                All review steps not yet completed.
                            </div>
                        }
                    >
                        <Button iconName="status-info" variant="icon"/>
                    </Popover>}
            </SpaceBetween>
        );
    };
    const approverButtons = () => {
        return (
            <SpaceBetween direction="horizontal" size="xs">
                <Button disabled={obligationStatus !== OBLIGATION_STATUS.UnderApproval}
                        onClick={rejectButtonClickHandler}>Reject</Button>
                <Button disabled={isApproveButtonDisable()}
                        onClick={approveButtonClickHandler}>Approve</Button>
                {!(checklistState.approver.option1 && checklistState.approver.option2 && checklistState.approver.option3 && checklistState.approver.option4) &&
                    <Popover
                        position="top"
                        size="small"
                        triggerType="custom"
                        content={
                            <div>
                                All steps not yet completed.
                            </div>
                        }
                    >
                        <Button iconName="status-info" variant="icon"/>
                    </Popover>}
            </SpaceBetween>
        );
    }

    const reviewerApproverButtons = ({userRole}) => {
        let content;
        if (userRole === REVIEWER_ROLE) {
            content = reviewerButtons();
        } else if (userRole === APPROVER_ROLE) {
            content = approverButtons();
        } else if (userRole === ADMINISTRATOR_ROLE) {
            content = (
                <SpaceBetween direction="horizontal" size="xs">
                    {reviewerButtons()}
                    {approverButtons()}
                </SpaceBetween>
            );
        }
        return content;
    }

    return (
        <>
            <div>
                <Flashbar items={dstStateMap?.flashBarItem}/>
                <Flashbar items={updateWorkflowStatus?.flashBarItem}/>
                <SpaceBetween size='l'>
                    <div className="progress-card">
                        <ul id="progressbar">
                            <li className="active clickable" id="ingestion" onClick={onIngestionClickHandler}>
                                <strong>Ingestion</strong></li>
                            <li className={obligationStatus === OBLIGATION_STATUS.UnderApproval || obligationStatus === OBLIGATION_STATUS.UnderFiling || obligationStatus === OBLIGATION_STATUS.UnderAdjustment || obligationStatus === OBLIGATION_STATUS.Filed ? "active" : obligationStatus === OBLIGATION_STATUS.UnderReview ? "semi-active" : ""}
                                id="review"><strong>Review</strong></li>
                            <li id="approve"
                                className={obligationStatus === OBLIGATION_STATUS.UnderApproval ? "semi-active" : obligationStatus === OBLIGATION_STATUS.UnderAdjustment || obligationStatus === OBLIGATION_STATUS.UnderFiling || obligationStatus === OBLIGATION_STATUS.Filed ? "active" : ""}>
                                <strong>Approve</strong></li>
                            <li id="filed" style={{width: '0'}}
                                className={obligationStatus === OBLIGATION_STATUS.Filed ? "active" : ""}>
                                <strong>Complete</strong></li>
                        </ul>
                    </div>

                    <ExpandableSection
                        variant="container"
                        header={<Header actions={<SpaceBetween direction="horizontal" size="xs">
                            <div onClick={(e) => {
                                e.stopPropagation()
                            }}>
                            </div>
                            {reviewerApproverButtons({userRole})}
                        </SpaceBetween>}>Obligation details</Header>}
                    >
                        <Grid gridDefinition={[{colspan: 6}, {colspan: 6}]}>
                            <ObligationDetails data={itemData} obligationStatus={obligationStatus}/>
                            <ExceptionSummary exceptionSummaryTableProp={exceptionSummaryTableProp}/>
                        </Grid>
                    </ExpandableSection>
                    <div>
                        <Tabs
                            tabs={[
                                {
                                    label: 'Summary',
                                    id: 'first',
                                    content: <DSTSummary
                                        parentWorkflowId={itemData?.parentWorkflowId}
                                        useCase={itemData?.phastosUseCaseName}
                                        lastUpdatedOn={lastUpdatedOn}
                                        setLastUpdatedOn={setLastUpdatedOn}
                                        setEntityFilter={setEntityFilter}
                                        setActiveTabId={setActiveTabId}
                                        entityBreakDownTabId={ENTITY_BREAKDOWN_TAB_ID}
                                    />
                                },
                                {
                                    label: 'Entity Breakdown',
                                    id: 'second',
                                    content: <EntityBreakdown
                                        parentWorkflowId={itemData?.parentWorkflowId}
                                        useCase={itemData?.phastosUseCaseName}
                                        lastEvaluatedKey={lastEvaluatedKey}
                                        setLastEvaluatedKey={setLastEvaluatedKey}
                                        lastUpdatedOn={lastUpdatedOn}
                                        setLastUpdatedOn={setLastUpdatedOn}
                                        entityFilter={entityFilter}
                                        dstDispatcher={dstDispatcher}
                                    />
                                },
                                {
                                    label: 'Attachments',
                                    id: 'third',
                                    content: <ViewAttachments
                                        parentWorkflowId={itemData?.parentWorkflowId}
                                        useCase={itemData?.type}
                                    />,
                                    disabled: false
                                },
                                {
                                    label: 'Audit',
                                    id: 'fourth',
                                    content: <DSTAdjustmentAudit
                                        parentWorkflowId={itemData?.parentWorkflowId}
                                        dstDispatcher={dstDispatcher}/>,
                                },
                                {
                                    label: 'Information',
                                    id: 'fifth',
                                    content: <DSTInformation/>
                                },
                                {
                                    label: 'Checklists',
                                    id: 'sixth',
                                    content: <DSTChecklists
                                        isUnderReview={isUnderReview}
                                        stepsState={stepsState}
                                        setStepsStateHandler={setStepStateHandler}
                                        updateWorkflowStatusDispatcher={updateWorkflowStatusDispatcher}
                                        parentWorkflowId={itemData?.parentWorkflowId}
                                        setActiveTabId={setActiveTabId}
                                        obligationStatus={obligationStatus}
                                        checklistState={checklistState}
                                        setChecklistState={setChecklistState}
                                        userRole={userRole}/>
                                },

                            ]}
                            activeTabId={activeTabId}
                            onChange={(event) => {
                                setEntityFilter(null)
                                setActiveTabId(event.detail.activeTabId)
                            }}
                        />
                    </div>
                </SpaceBetween>
                <RejectReturnModal
                    rejectConfirmModal={rejectConfirmModal}
                    rejectConfirmHandler={rejectConfirmHandler}
                    setRejectConfirmModal={setRejectConfirmModal}
                />
            </div>
        </>
    );
}

export default DSTException;